import {
  allLeavesOfType,
  childNodesOfType,
  leavesOfType,
} from 'components/utility/TreeHelpers'
import { makeContentServicePostRequest } from 'infrastructure/contentService'
import {
  appendRowsToSpreadsheet,
  createSpreadsheet,
  highlightSpreadsheetColumns,
  setSpreadsheetWrapStrategy,
  WrapStrategy,
} from 'infrastructure/googleDrive'
import { INode } from 'shared/types/tree'
import { LeafTypes, NavigableNodeTypes, NodeType } from 'shared/types/ingestion'
import { getStandardsMapFromIds } from 'components/tree/standardUtilities'
import { createIdMap } from 'components/version_mapping/utility'
import { closestNavigableParent } from 'components/utility/TreeNavigation'
import { directTreeAccessUrl } from 'infrastructure/api'
import {
  TransformedApplicableStandard,
  FlowResultsParseResult,
  AnalyzeInputResult,
} from './types'
import { ContentServiceFlowResults } from '../../contentService/types'
import {
  StandardsImportApplicableStandards,
  StandardsImportErrors,
} from './types'

function stripHTML(html: string) {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.innerText
}

async function createRowsFromKiddomTree(tree: INode): Promise<string[][]> {
  const headers: string[] = [
    'Addressing',
    'Building On',
    'Building Toward',
    'Custom',
    'Existing Standards',
    'node id',
    'link to node',
  ]
  const rows: string[][] = []

  const allUniqueStandardIDs = new Set(
    allLeavesOfType(tree, LeafTypes.Skill).map((s) => {
      return s.body.skillID
    }),
  )
  const standardsMap = await getStandardsMapFromIds(
    Array.from(allUniqueStandardIDs),
  )
  const idMap = createIdMap(tree)

  let currentLevel = 1
  function recursivelyMakeRows(node: INode, ancestors: string[] = []) {
    if (
      (node.title && NavigableNodeTypes.includes(node.type as NodeType)) ||
      node.type === NodeType.Question
    ) {
      const parent = closestNavigableParent(node, idMap)

      const linkToNode = directTreeAccessUrl({
        treeId: node.tree_id,
        versionId: node.version,
        nodeId: parent.id,
      })

      const existingStandardLists = childNodesOfType(node, NodeType.SkillsList)
      const existingStandardIDs = existingStandardLists
        .flatMap((list) => leavesOfType(list, LeafTypes.Skill))
        .map((s) => s.body.skillID)
      const existingStandards = existingStandardIDs
        .map((id) => standardsMap[id])
        .join(', ')

      const nodeText =
        node.type === NodeType.Question && node.description
          ? stripHTML(node.description).substring(0, 100)
          : node.title
      const newRow = [
        '',
        '',
        '',
        '',
        existingStandards,
        node.id,
        linkToNode,
        ...ancestors,
        nodeText,
      ]
      if (newRow.length > headers.length) {
        headers.push(`Level ${currentLevel}`)
        currentLevel++
      }
      rows.push(newRow)
    }

    node.nodes?.forEach((n) => {
      recursivelyMakeRows(n, [...ancestors, node.title])
    })
  }

  recursivelyMakeRows(tree)

  return [headers, ...rows]
}

export async function exportKiddomTreeToSpreadsheet(
  tree: INode,
  accessToken: string,
): Promise<string> {
  const spreadsheetId = await createSpreadsheet(
    accessToken,
    `${tree.title}-standards`,
  )
  const rows = await createRowsFromKiddomTree(tree)
  await appendRowsToSpreadsheet(accessToken, spreadsheetId, rows)
  await highlightSpreadsheetColumns(accessToken, spreadsheetId, [0, 1, 2, 3])
  await setSpreadsheetWrapStrategy(
    accessToken,
    spreadsheetId,
    WrapStrategy.CLIP,
  )
  return spreadsheetId
}

// Flow-based API calls
/**
 * Starts a flow to analyze CSV data for bulk standards import.
 *
 * @param body - The request parameters
 * @param body.treeId - The tree ID to add standards to
 * @param body.version - The version ID of the tree
 * @param body.content - Base64-encoded CSV content from Google Sheets
 * @param body.standardState - The state curriculum identifier (e.g., 'CA', 'TX')
 *
 * @returns Promise<string> - The flow ID of the started flow
 *
 * Response structure:
 * {
 *   "data": {
 *     "flow_id": string,
 *     "job_type": "bulk_add_standards/input",
 *     "message": string
 *   }
 * }
 */
export async function importViaContentServiceFlow(body: {
  treeId: string
  version: string
  content: string
  standardState: string
}): Promise<string> {
  const response = await makeContentServicePostRequest(
    'content/v1/bulk_add_standards/input/job/start',
    {
      tree_id: body.treeId,
      version_id: body.version,
      csv_data: body.content,
      state: body.standardState,
    },
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Import flow start failed with status ${response.status}: ${errorText}`,
    )
  }

  const data = await response.json()
  if (!data.data?.flow_id) {
    throw new Error('No flow ID received from import API')
  }

  return data.data.flow_id
}

/**
 * Starts a flow to add standards to the tree based on analysis results.
 *
 * @param body - The request parameters
 * @param body.tree_id - The tree ID to add standards to
 * @param body.version_id - The version ID of the tree
 * @param body.node_ids - List of node data with standards to add
 *
 * @returns Promise<string> - The flow ID of the started flow
 *
 * Response structure:
 * {
 *   "data": {
 *     "flow_id": string,
 *     "job_type": "bulk_add_standards/add",
 *     "message": string
 *   }
 * }
 */
export async function addViaContentServiceFlow(body: {
  tree_id: string
  version_id: string
  node_ids: TransformedApplicableStandard[]
}): Promise<string> {
  const response = await makeContentServicePostRequest(
    'content/v1/bulk_add_standards/add/job/start',
    body,
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Add standards flow start failed with status ${response.status}: ${errorText}`,
    )
  }

  const data = await response.json()
  if (!data.data?.flow_id) {
    throw new Error('No flow ID received from add API')
  }

  return data.data.flow_id
}

/**
 * Parses flow results to extract applicable standards and errors.
 *
 * For the bulk add standards input flow:
 * - The worker job calls analyze_input and returns the aggregated results
 * - The dependant_results array contains individual node results from add_standards calls
 * - We need to extract data from the worker results for the input flow
 *
 * @param flowResults - The flow results from the content service
 * @returns Object containing applicable standards and errors arrays
 */
export function parseFlowResults(
  flowResults: ContentServiceFlowResults,
): FlowResultsParseResult {
  const applicableStandards: StandardsImportApplicableStandards[] = []
  const errors: StandardsImportErrors[] = []

  // For bulk add standards input flow, the data comes from the worker results
  // (the analyze_input function returns aggregated results)
  const workerResults = flowResults.data.worker_results || []

  for (const workerResult of workerResults) {
    if (workerResult.result) {
      const resultData = workerResult.result as unknown

      // Use type guard to safely check for analyze_input result format
      if (isAnalyzeInputResult(resultData)) {
        applicableStandards.push(...resultData.data)
        errors.push(...resultData.errors)
      }
    }
  }

  // Also check dependant results for any additional data
  // (though for input flow, this should be empty)
  const dependantResults = flowResults.data.dependant_results || []

  for (const dependantResult of dependantResults) {
    if (dependantResult.result) {
      const resultData = dependantResult.result as unknown

      // Use type guard to safely check for analyze_input result format
      if (isAnalyzeInputResult(resultData)) {
        applicableStandards.push(...resultData.data)
        errors.push(...resultData.errors)
      }
    }
  }

  return {
    applicableStandards,
    errors,
  }
}

/**
 * Transforms applicable standards data from input flow format to add flow format.
 *
 * Input format (from analyze_input):
 * {
 *   "node_id": "12345",
 *   "node_title": "node title",
 *   "node_link": "https://whatever",
 *   "skills_lists": [
 *     {
 *       "skills_title": "Addressing",
 *       "standards": [
 *         {
 *           "standard_name": "a.b.c",
 *           "standard_id": 12345
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * Output format (for add_standards):
 * {
 *   "node_id": "12345",
 *   "skills_lists": [
 *     {
 *       "skills_title": "Addressing",
 *       "standard_ids": [12345, 67890]
 *     }
 *   ]
 * }
 *
 * @param applicableStandards - Array of applicable standards from input flow
 * @returns Array of transformed node data for add flow
 */
export function transformApplicableStandardsForAddFlow(
  applicableStandards: StandardsImportApplicableStandards[],
): TransformedApplicableStandard[] {
  return applicableStandards.map((node) => ({
    node_id: node.node_id,
    skills_lists: node.skills_lists.map((skillsList) => ({
      skills_title: skillsList.skills_title,
      standard_ids: skillsList.standards.map((standard) =>
        Number(standard.standard_id),
      ),
    })),
  }))
}

/**
 * Type guard to check if a result contains analyze_input data
 */
function isAnalyzeInputResult(result: unknown): result is AnalyzeInputResult {
  return (
    result !== null &&
    typeof result === 'object' &&
    'data' in result &&
    'errors' in result &&
    Array.isArray((result as Record<string, unknown>).data) &&
    Array.isArray((result as Record<string, unknown>).errors)
  )
}
